import { fetchWithAuth, parseJsonResponse } from './api'

export type ChartAlertMetric = 'price' | 'oi'
export type ChartAlertDelivery = 'telegram' | 'pushover' | 'emergency'
export interface ChartAlertDraft {
  metric: ChartAlertMetric
  exchange: string
  threshold: number
  market?: 'spot' | 'futures'
}
export interface ChartAlertNotification {
  notification_channel: 'telegram' | 'pushover'
  pushover_priority: 0 | 1 | 2
}
export interface ChartLevelAlert extends ChartAlertDraft, ChartAlertNotification {
  id: string
  symbol: string
  direction: 'above' | 'below'
  status: 'active' | 'triggered' | 'cancelled'
  created_at: number
  triggered_at: number | null
  triggered_value?: number | null
  last_value?: number | null
  delivered_at?: number | null
  delivery_status?: 'pending' | 'sent' | null
  delivery_error?: string | null
}
export interface ChartAlertAvailability {
  ready: boolean
  error: string | null
}
export interface ChartAlertsResponse {
  alerts: ChartLevelAlert[]
  delivery_options: {
    telegram: ChartAlertAvailability
    pushover: ChartAlertAvailability
  }
}

export const CHART_ALERT_DELIVERIES: { value: ChartAlertDelivery; label: string }[] = [
  { value: 'telegram', label: 'Telegram' },
  { value: 'pushover', label: 'Pushover' },
  { value: 'emergency', label: 'Emergency Pushover' },
]

export function chartAlertNotification(delivery: ChartAlertDelivery): ChartAlertNotification {
  return { notification_channel: delivery === 'telegram' ? 'telegram' : 'pushover', pushover_priority: delivery === 'emergency' ? 2 : 0 }
}

export function chartAlertDelivery(alert: ChartAlertNotification): ChartAlertDelivery {
  return alert.notification_channel === 'telegram' ? 'telegram' : alert.pushover_priority === 2 ? 'emergency' : 'pushover'
}

export function formatChartAlertLevel(alert: Pick<ChartLevelAlert, 'metric' | 'threshold'>): string {
  const value = alert.threshold.toLocaleString(undefined, { maximumSignificantDigits: 10 })
  return alert.metric === 'price' ? `$${value}` : `${value} coins`
}

async function request<T>(url: string, options?: RequestInit): Promise<T> {
  const response = await fetchWithAuth(url, options)
  const data = await parseJsonResponse<T & { detail?: unknown; error?: string }>(response)
  if (!response.ok) throw new Error(data.error || (typeof data.detail === 'string' ? data.detail : 'Could not save your chart alert. Please try again.'))
  return data
}

export function getChartAlerts(): Promise<ChartAlertsResponse> {
  return request('/api/screener/alerts', { cache: 'no-store' })
}

export function createChartAlert(draft: ChartAlertDraft & ChartAlertNotification & { symbol: string }): Promise<{ alert: ChartLevelAlert }> {
  return request('/api/screener/alerts', { method: 'POST', body: JSON.stringify(draft) })
}

export function updateChartAlert(id: string, patch: Partial<ChartAlertNotification> & { threshold?: number }): Promise<{ alert: ChartLevelAlert }> {
  return request(`/api/screener/alerts/${id}`, { method: 'PATCH', body: JSON.stringify(patch) })
}

export function cancelChartAlert(id: string): Promise<{ alert: ChartLevelAlert }> {
  return request(`/api/screener/alerts/${id}`, { method: 'DELETE' })
}
